import React, { useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';

import Alert from '@material-ui/lab/Alert';
import { DialogBox, PrintProgressLoader } from '@mummssoftware/common-ui';
import type {
  FaxInstructions,
  EmailInstructions,
} from '@mummssoftware/common-ui';
import {
  PimMessagingContext,
  SET_IDG_RENDER_INSTRUCTIONS,
  notifyPIMDownloadIDGComplete,
} from '@mummssoftware/pim-messenger';
import {
  ConfigService,
  loadIdgComponentsPrintConfig,
  Printing,
} from '@mummssoftware/utils';
import { isFaxStatus, PrintProgress } from '@mummssoftware/utils/api/Printing';
import { parentheses } from '@mummssoftware/utils/formatters';

import { Context } from '../../context';
import defaults from '../../translations/en.json';
import { IdgPdfBlobViewer } from './IdgPdfBlobViewer';

const isPdfBufferResponse = (x: unknown): x is number[] =>
  Array.isArray(x) && !isFaxStatus(x);

const pdfResToBlob = (x: any) =>
  new Blob([new Uint8Array(x)], {
    type: 'application/pdf',
  });

type IDGRenderDialogProps = {
  instructions: any; // todo PIM payload type
};

export const IDGRenderDialog = ({ instructions }: IDGRenderDialogProps) => {
  const { dispatch } = useContext(PimMessagingContext);
  const {
    keycloak,
    agency,
    siteId,
    currentUser,
    patient,
    observation,
    idgMeeting,
  } = useContext(Context);

  const [
    manifestRecord,
    setManifestRecord,
  ] = useState<mumms.GenericForm | null>(null);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);

  const [loadingRender, setLoadingRender] = useState(true);
  const [renderError, setRenderError] = useState(false);
  const [printProgress, setPrintProgress] = useState<PrintProgress | null>(
    null,
  );

  const printingService = new Printing({
    url: window.env.hosts.printingService,
    keycloak,
    agency,
    currentUser,
  });

  const configService = (() => {
    const url = (window.env.hosts as any).configService as string | undefined;
    if (!url || !keycloak || !agency) return null;
    return new ConfigService({ url, keycloak, agency });
  })();

  const saveIDGRecord = (idgInstructions: any) => {
    const { meeting_id, agenda_start, agenda_stop } = idgInstructions;
    return observation.savePatientManifestIDG(
      (patient as mumms.patient).patientNumber,
      String(currentUser.userId),
      meeting_id,
      agency,
      siteId as string,
      agenda_start,
      agenda_stop,
    );
  };

  const { meeting_id } = instructions;

  const printIDGFormAndLinkDocumentArchive = (
    formDto: mumms.GenericForm,
    idgPreconfig: Record<string, unknown>,
  ) =>
    printingService.printDaChart(
      {
        ...idgPreconfig,
        ...(formDto.supplementaltitle
          ? { supplementaltitle: formDto.supplementaltitle }
          : {}),
        ...(meeting_id ? { meeting_id: String(meeting_id) } : {}),
      },
      patient as mumms.patient,
      `IDG Meeting ${parentheses(formDto.supplementaltitle)}`,
      setPrintProgress,
      undefined,
      formDto,
    );

  const fetchManifestById = (id: any) =>
    observation.getFajitaFormByIdByMode(Number(id), undefined, agency);

  useEffect(() => {
    if (!patient) return;
    const dates =
      idgMeeting?.from && idgMeeting?.to
        ? {
            fromDate: moment(idgMeeting.from, 'MM-DD-YYYY').format(
              'YYYY-MM-DD',
            ),
            toDate: moment(idgMeeting.to, 'MM-DD-YYYY').format('YYYY-MM-DD'),
          }
        : undefined;
    loadIdgComponentsPrintConfig(configService, dates)
      .then((idgPreconfig) => {
        if (!idgPreconfig) {
          setRenderError(true);
          return null;
        }
        return saveIDGRecord(instructions).then((formDto) => {
          setManifestRecord(formDto);
          return printIDGFormAndLinkDocumentArchive(formDto, idgPreconfig).then(
            (pdfRes) => ({
              formDto,
              pdfRes,
            }),
          );
        });
      })
      .then(async (result) => {
        if (!result) return;
        const { formDto, pdfRes } = result;
        if (isPdfBufferResponse(pdfRes)) {
          setPdfBlob(pdfResToBlob(pdfRes));
          const manifestWithArchive = await fetchManifestById(formDto.id);
          setManifestRecord(manifestWithArchive);
        }
      })
      .finally(() => {
        setLoadingRender(false);
      });
  }, []);

  const printOrFaxDocument = useCallback(
    (
      faxInstructions: FaxInstructions | undefined,
      emailInstructions: EmailInstructions | undefined,
    ) =>
      printingService.printManifest(
        [manifestRecord as mumms.FormDTO], // todo verify dto types are right
        patient as mumms.patient,
        false,
        undefined,
        faxInstructions,
        emailInstructions,
      ),
    [printingService, patient, manifestRecord],
  );

  const closeDialog = () => {
    dispatch({
      type: SET_IDG_RENDER_INSTRUCTIONS,
      payload: null,
    });
    notifyPIMDownloadIDGComplete();
  };

  // todo furnish modal with stable dimensions and richer loading view
  return (
    <>
      {loadingRender ? (
        <>
          {createPortal(
            <PrintProgressLoader
              progress={printProgress}
              sourceString="IDG Meeting"
            />,
            document.getElementById('portal-container') || document.body,
          )}
        </>
      ) : (
        <DialogBox
          title={defaults.viewIDGDocument as string}
          disableBackdropClick
          open
          handleClick={closeDialog}
          fullWidth
          maxWidth="md"
          PaperProps={{
            style: {
              height: 'calc(100vh - 48px)',
              maxHeight: 'calc(100vh - 48px)',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
          contentStyle={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {pdfBlob && manifestRecord && !renderError ? (
            <IdgPdfBlobViewer
              file={pdfBlob}
              getPrintPromise={printOrFaxDocument}
            />
          ) : (
            <Alert severity="error">{defaults.idgMeetingDocumentError}</Alert>
          )}
        </DialogBox>
      )}
    </>
  );
};
